import { useState } from 'react';
import { ErrorNotice, Modal, Time, useToast } from '../ui/kit';
import { configDiff, formatChangeValue, GROUP_LABELS } from './diff';
import { useDraft } from './draft';
import { useDraftAuthor } from './draftAuthor';

/**
 * Draft status and the save / publish controls, shared by every config
 * screen. States, in order of precedence: problems to fix → unsaved edits →
 * saved draft newer than what is live → live.
 *
 * A saved draft that is not live says who saved it and when, with "See
 * changes" (a field-by-field diff against what is live) and "Discard draft"
 * (back to what is live, `POST /config/discard-draft`) — third live review,
 * 2026-09-27: "Draft v4 is saved but not live" showed on every settings page
 * with no way to tell what v4 was or to get rid of it.
 */
export function PublishBar({ hasTestKey }: { hasTestKey: boolean }) {
  const d = useDraft();
  const toast = useToast();
  const [mirror, setMirror] = useState(true);
  const dirty = d.dirtyGroups.length > 0;
  const draftVersion = d.draftMeta?.version ?? 0;
  const liveVersion = d.publishedMeta?.published ? d.publishedMeta.version : null;
  const unpublished = draftVersion > 0 && draftVersion !== liveVersion;
  const blocked = d.clientIssues.length > 0;
  const author = useDraftAuthor(d.siteKey, unpublished ? draftVersion : null, d.draftMeta?.updatedBy ?? null, unpublished);
  const [showDiff, setShowDiff] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const changes = unpublished && d.published ? configDiff(d.published, d.base) : [];

  let status: string;
  if (blocked) status = `${d.clientIssues.length} ${d.clientIssues.length === 1 ? 'field needs' : 'fields need'} attention`;
  else if (dirty) status = `Unsaved changes: ${d.dirtyGroups.map((g) => GROUP_LABELS[g]).join(', ')}`;
  else if (unpublished) status = `Draft v${draftVersion} is saved but not live`;
  else if (liveVersion) status = `Live: v${liveVersion}`;
  else status = 'Nothing published yet — the widget uses its defaults';

  return (
    <div className="stack-sm">
      {d.actionError && d.actionError.code !== 'VALIDATION_FAILED' ? <ErrorNotice error={d.actionError} /> : null}
      <div className="publishbar" role="region" aria-label="Save and publish">
        <div className="stack-sm" style={{ gap: 2 }}>
          <span className="version-chip" aria-live="polite">
            <span className={`dotstat ${blocked ? 'bad' : dirty || unpublished ? 'warn' : liveVersion ? 'ok' : ''}`}>{status}</span>
          </span>
          {unpublished ? (
            <span className="small draft-info">
              <span>
                {/* The status line above already says "Draft vN"; this says who and when. */}
                {dirty || blocked ? `Draft v${draftVersion} saved` : 'Saved'}
                {author ? <> by {author}</> : null}
                {d.draftMeta?.updatedAt ? (
                  <>
                    , <Time iso={d.draftMeta.updatedAt} />
                  </>
                ) : null}
              </span>
              <span className="draft-actions">
                <button type="button" className="btn-link" onClick={() => setShowDiff(true)}>
                  See changes
                </button>
                {liveVersion ? (
                  <>
                    <span aria-hidden="true"> · </span>
                    <button type="button" className="btn-link danger" onClick={() => setConfirmDiscard(true)} disabled={d.discarding || d.publishing || d.saving}>
                      Discard draft
                    </button>
                  </>
                ) : null}
              </span>
            </span>
          ) : null}
          <span className="small muted">
            {liveVersion ? (
              <>
                v{liveVersion} live{d.publishedMeta?.updatedAt ? <> · written <Time iso={d.publishedMeta.updatedAt} /></> : null}. Widgets pick up a
                change within a minute.
              </>
            ) : (
              'Publishing makes this configuration live on every page with your key.'
            )}
          </span>
        </div>
        <div className="row">
          {hasTestKey ? (
            <label className="check small" style={{ alignItems: 'center' }} htmlFor="publish-mirror-test">
              <input id="publish-mirror-test" type="checkbox" checked={mirror} onChange={(e) => setMirror(e.target.checked)} />
              <span>Also publish to the test key</span>
            </label>
          ) : null}
          {dirty ? (
            <button type="button" className="btn btn-ghost" onClick={d.reset} disabled={d.saving || d.publishing}>
              Discard
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            disabled={!dirty || blocked || d.saving || d.publishing}
            onClick={async () => {
              const outcome = await d.saveOutcome();
              if (outcome === 'saved') toast('Draft saved');
              else if (outcome === 'unchanged') toast('No changes to save');
            }}
          >
            {d.saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked || d.saving || d.publishing || (!dirty && !unpublished && liveVersion !== null)}
            onClick={async () => {
              if (await d.publish({ mirrorToTest: mirror })) toast('Published. Live widgets update within a minute.');
            }}
          >
            {d.publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
      <Modal
        open={showDiff}
        title={`What draft v${draftVersion} changes`}
        onClose={() => setShowDiff(false)}
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setShowDiff(false)}>
            Close
          </button>
        }
      >
        <p className="small muted" style={{ margin: 0 }}>
          {liveVersion ? `Compared with v${liveVersion}, which is live now.` : 'Compared with the defaults the widget uses until you publish.'}
          {dirty ? ' Unsaved edits on this page are not included.' : ''}
        </p>
        {changes.length ? (
          <div className="table-wrap">
            <table className="data diff-table">
              <caption className="visually-hidden">Changed fields</caption>
              <thead>
                <tr>
                  <th scope="col">Setting</th>
                  <th scope="col">Live</th>
                  <th scope="col">Draft</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.path}>
                    <th scope="row">
                      <span className="small faint">{GROUP_LABELS[c.group]}</span>
                      <br />
                      {c.label}
                    </th>
                    <td className="diff-before">{formatChangeValue(c.path, c.before)}</td>
                    <td className="diff-after">{formatChangeValue(c.path, c.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ margin: 0 }}>No differences: this draft has the same settings as what is live. Publishing it changes nothing a visitor sees.</p>
        )}
      </Modal>
      <Modal
        open={confirmDiscard}
        title={`Discard draft v${draftVersion}?`}
        onClose={() => setConfirmDiscard(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmDiscard(false)}>
              Keep the draft
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={d.discarding}
              onClick={async () => {
                if (await d.discardDraft()) {
                  setConfirmDiscard(false);
                  toast(`Draft discarded. The settings are back to what is live.`);
                } else {
                  setConfirmDiscard(false);
                }
              }}
            >
              {d.discarding ? 'Discarding…' : 'Discard draft'}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          The settings go back to v{liveVersion}, which is live now. {changes.length ? `${changes.length} ${changes.length === 1 ? 'change' : 'changes'} in this draft ${changes.length === 1 ? 'is' : 'are'} dropped` : 'The draft is dropped'}
          {dirty ? ', along with your unsaved edits on this page' : ''}. Visitors see no change. The audit log keeps a record.
        </p>
      </Modal>
    </div>
  );
}
